import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { useInstrumentResolver } from "../../../hooks/markets/useInstrumentResolver";
import { OrderHistoryItem } from "../../../interfaces/history";
import {
  DetailRow,
  FormattedPrice,
  FormattedTime,
  SizeWithUsd,
} from "./DetailRow";
import HistoryDetailsShell from "./HistoryDetailsShell";

interface IOrderHistoryDetailsModalProps {
  order?: OrderHistoryItem;
  onHide?: () => void;
  style?: CSSProperties;
}

function OrderHistoryDetailsModal({
  order,
  onHide,
  style,
}: IOrderHistoryDetailsModalProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "components.History.details.OrderHistoryDetailsModal",
  });
  const { resolveInstrumentName, resolveMarketPrecision } =
    useInstrumentResolver();

  const instrumentName = order
    ? resolveInstrumentName(order as any) || order.instrument_name
    : undefined;
  const { amount_precision, price_precision } = order
    ? resolveMarketPrecision(order as any)
    : { amount_precision: 2, price_precision: 2 };

  if (!order) {
    return (
      <HistoryDetailsShell show={false} onHide={onHide} style={style}>
        <span />
      </HistoryDetailsShell>
    );
  }

  const isBuy = order.side === SideResponse.Buy;
  const sideLabel = isBuy
    ? t("buy", { defaultValue: "Buy" })
    : t("sell", { defaultValue: "Sell" });

  const orderTypeLabel = (() => {
    const raw = String(order.order_type ?? "").toLowerCase();
    if (raw === OrderTypeResponse.Limit) return t("limit", { defaultValue: "Limit" });
    if (raw === OrderTypeResponse.Market) return t("market", { defaultValue: "Market" });
    return order.order_type ?? "-";
  })();

  const flags: string[] = [];
  if (order.post_only) flags.push(t("post_only", { defaultValue: "Post Only" }));
  if (order.reduce_only) flags.push(t("reduce_only", { defaultValue: "Reduce Only" }));

  // Order-history timestamps are in seconds.
  return (
    <HistoryDetailsShell
      show={!!order}
      onHide={onHide}
      instrumentName={instrumentName}
      copyId={{
        label: t("order_id", { defaultValue: "Order ID" }),
        value: order.order_id,
      }}
      style={style}
    >
      <DetailRow
        label={t("side", { defaultValue: "Side" })}
        value={
          <span
            style={{
              color: isBuy ? COLORS.up.one : COLORS.down.one,
            }}
          >
            {sideLabel}
          </span>
        }
      />
      <DetailRow
        label={t("order_type", { defaultValue: "Order Type" })}
        value={orderTypeLabel}
      />
      <DetailRow
        label={t("size_filled", { defaultValue: "Size / Filled" })}
        value={
          <SizeWithUsd
            amount={order.amount}
            filledAmount={order.filled}
            price={order.price}
            amountPrecision={amount_precision}
            pricePrecision={price_precision}
          />
        }
      />
      <DetailRow
        label={t("price", { defaultValue: "Price" })}
        value={
          <FormattedPrice value={order.price} precision={price_precision} />
        }
      />
      <DetailRow
        label={t("status", { defaultValue: "Status" })}
        value={
          order.order_status
            ? t(String(order.order_status).toLowerCase(), {
                defaultValue: String(order.order_status),
              })
            : null
        }
      />
      <DetailRow
        label={t("flags", { defaultValue: "Flags" })}
        value={flags.length ? flags.join(", ") : null}
      />
      <DetailRow
        label={t("date_time", { defaultValue: "Date & Time" })}
        value={<FormattedTime value={order.created_timestamp} unit="seconds" />}
      />
    </HistoryDetailsShell>
  );
}

export default OrderHistoryDetailsModal;
